import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import type { JSONSchemaInteger, JSONSchemaString } from '@sphere/platform';
import { SftpConfig, SftpProperty, SftpSchema } from './sftp-config.models';
import { SftpConfigComponentStore } from './sftp-config.store';
import { startWith, tap } from 'rxjs/operators';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { LetDirective } from '@ngrx/component';
import { MatButton } from '@angular/material/button';

export type SftpFieldConfig = FormlyFieldConfig & {
    defaultValue?: SftpConfig;
    props: {
        schema: SftpSchema;
        wrappedStyles?: boolean;
        hideHeader?: boolean;
        enableConnectionTest?: boolean;
    };
};

@Component({
    selector: 'sphere-sftp-config',
    templateUrl: './sftp-config.component.html',
    styleUrls: ['./sftp-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SftpConfigComponentStore],
    imports: [ReactiveFormsModule, NgClass, MatFormField, MatLabel, MatInput, MatHint, MatError, NgTemplateOutlet, LetDirective, MatButton]
})
export class SftpConfigComponent extends FieldType<SftpFieldConfig> implements OnInit, AfterViewInit {
    store = inject(SftpConfigComponentStore);

    private readonly destroyRef = inject(DestroyRef);
    public sftpForm!: FormGroup;

    ngOnInit() {
        const { Host, Port, RemoteFolder, Username, Password } = (this.formControl.value as SftpConfig) || {};

        this.sftpForm = new FormGroup({});

        if (this.props.schema.properties.Host) {
            this.buildControl('Host', this.props.schema.properties.Host, Host);
        }
        if (this.props.schema.properties.Port) {
            this.buildControl('Port', this.props.schema.properties.Port, Port);
        }
        if (this.props.schema.properties.RemoteFolder) {
            this.buildControl('RemoteFolder', this.props.schema.properties.RemoteFolder, RemoteFolder);
        }
        if (this.props.schema.properties.Username) {
            this.buildControl('Username', this.props.schema.properties.Username, Username);
        }
        if (this.props.schema.properties.Password) {
            this.buildControl('Password', this.props.schema.properties.Password, Password);
        }
    }

    ngAfterViewInit() {
        this.sftpForm.valueChanges
            .pipe(
                startWith(this.sftpForm.getRawValue()),
                takeUntilDestroyed(this.destroyRef),
                tap(sftpConfig => {
                    this.formControl.setValue(sftpConfig);
                })
            )
            .subscribe();
        this.sftpForm.statusChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(status => {
                    console.debug(`sftpForm`, status);
                })
            )
            .subscribe();
    }

    private buildControl(
        name: SftpProperty,
        schema: (JSONSchemaString & { default?: string }) | (JSONSchemaInteger & { default?: number }),
        value?: string | number
    ) {
        if (!name) {
            throw new Error(`missing control name for schema ${schema}`);
        }
        if (!schema) {
            throw new Error(`missing control schema for field name ${name}`);
        }
        if (schema.type !== 'string' && schema.type !== 'integer') {
            throw new Error(`invalid form control type ${schema} for control ${name}`);
        }
        let control: FormControl<string | number>;
        if (schema.type === 'string') {
            control = this.buildStringControl(name, schema, value as string) as any;
            if (control) {
                this.sftpForm.addControl(name, control);
            }
        } else if (schema.type === 'integer') {
            control = this.buildIntegerControl(name, schema, value as number) as any;
            if (control) {
                this.sftpForm.addControl(name, control);
            }
        }
    }

    private buildStringControl(name: SftpProperty, schema: JSONSchemaString & { default?: string }, value?: string) {
        const validators: ValidatorFn[] = [];
        if (this.isRequired(name)) {
            validators.push(Validators.required);
        }
        if (schema.pattern) {
            validators.push(Validators.pattern(schema.pattern));
        }
        if (schema.minLength) {
            validators.push(Validators.minLength(schema.minLength));
        }
        if (schema.maxLength) {
            validators.push(Validators.minLength(schema.maxLength));
        }
        return new FormControl<string>((value as string) || schema.default || '', validators);
    }

    private buildIntegerControl(name: SftpProperty, schema: JSONSchemaInteger & { default?: number }, value?: number) {
        const validators: ValidatorFn[] = [];
        if (this.isRequired(name)) {
            validators.push(Validators.required);
        }
        if (schema.minimum || schema.minimum === 0) {
            validators.push(Validators.min(schema.minimum));
        }
        if (schema.maximum || schema.maximum === 0) {
            validators.push(Validators.max(schema.maximum));
        }
        return new FormControl<number>(value || schema.default || (null as any), validators);
    }

    private isRequired(controlName: SftpProperty) {
        try {
            return this.props.schema.required.includes(controlName);
        } catch (e) {
            return false;
        }
    }

    public hasField(controlName: SftpProperty) {
        try {
            return Object.prototype.hasOwnProperty.call(this.props.schema.properties, controlName);
        } catch (e) {
            return false;
        }
    }

    public testConnection() {
        this.store.testConnection(this.sftpForm.getRawValue());
    }
}
